/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { bool, func, string, shape } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { getLimitDate } from "util/date";
import { validateCUITWithRegex } from "util/form";
import { selectors as sessionSelectors } from "reducers/session";
import { DateRangeField } from "pages/_components/fields/DateRangeField";
import Selector from "pages/_components/fields/formik/Selector";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";

const FORM_ID = "vep.filter";

const FilterForm = ({ isVepInProgress, onSubmit, loading, activeEnvironment }) => {
    const formRef = useRef(null);
    const [vepTypes, setVepTypes] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    const [vepMaxLength, setVepMaxLength] = useState(null);

    const initialValues = {
        queryType: "",
        cuitOrigin: "",
        cuitTaxpayer: "",
        vepId: "",
        dateRange: [null, null],
    };

    useEffect(() => {
        if (!vepTypes.length) {
            const types = configUtils.get("vep.link.types");
            if (types && types.split("|").length > 0) {
                setVepTypes(types.split("|"));
            }
        }
        const maxLength = configUtils.get("vep.number.max.length", 12);
        setVepMaxLength(maxLength);
    }, []);

    const handleSubmit = (values) => {
        const dateFrom = values?.dateRange[0] !== null ? values?.dateRange[0].toISOString().split("T")[0] : null;
        const dateTo = values?.dateRange[1] !== null ? values?.dateRange[1].toISOString().split("T")[0] : null;
        onSubmit({ ...values, pageNumber: 1, dateFrom, dateTo });
    };

    const handleReset = () => {
        formRef.current.setFieldValue("cuitOrigin", "");
        formRef.current.setFieldValue("cuitTaxpayer", "");
        formRef.current.setFieldValue("vepId", "");
        formRef.current.setFieldValue("dateRange", [null, null]);
    };

    const handleChangeType = (value) => {
        handleReset();
        setSelectedType(value);
    };

    const validateDateRange = (value) => (value[0] && value[1]) || (value[0] == null && value[1] == null);

    const vepValidation = Yup.lazy(() => {
        const validate = {
            queryType: Yup.string()
                .required(i18n.get("global.error.required"))
                .test("queryType", i18n.get("global.error.required"), (value) => value !== ""),
            cuitOrigin:
                selectedType && (selectedType === vepTypes[1] || selectedType === vepTypes[2])
                    ? Yup.string()
                          .required(i18n.get("global.error.required"))
                          .test(
                              "cuitOrigin",
                              i18n.get("vep.cuit.invalid.error", "Ingresá un CUIT/CUIL válido"),
                              (value) => validateCUITWithRegex(value),
                          )
                          .test(
                              "cuitOrigin",
                              i18n.get(
                                  "vep.cuit.validation.error.user",
                                  "El CUIT/CUIL debe ser distinto al de tu usuario",
                              ),
                              (value) => activeEnvironment && value !== activeEnvironment?.activeUserCuil,
                          )
                          .test(
                              "cuitOrigin",
                              i18n.get(
                                  "vep.cuit.validation.error.company",
                                  "El CUIT/CUIL debe ser distinto al de tu empresa",
                              ),
                              (value) => activeEnvironment && value !== activeEnvironment?.activeEnvironmentDocument,
                          )
                    : Yup.string().notRequired(),
            cuitTaxpayer:
                selectedType && selectedType === vepTypes[2]
                    ? Yup.string()
                          .required(i18n.get("global.error.required"))
                          .test(
                              "cuitTaxpayer",
                              i18n.get("vep.cuit.invalid.error", "Ingresá un CUIT/CUIL válido"),
                              (value) => validateCUITWithRegex(value),
                          )
                          .test(
                              "cuitTaxpayer",
                              i18n.get(
                                  "vep.cuit.validation.error.company",
                                  "El CUIT/CUIL debe ser distinto al de tu empresa",
                              ),
                              (value) => activeEnvironment && value !== activeEnvironment?.activeEnvironmentDocument,
                          )
                    : Yup.string().notRequired(),
            vepId:
                selectedType && selectedType === vepTypes[2]
                    ? Yup.string()
                          .required(i18n.get("global.error.required"))
                          .max(vepMaxLength, i18n.get("vep.number.invalid.error", "Ingresá un número de VEP válido"))
                    : Yup.string().notRequired(),
            dateRange: Yup.array()
                .of(Yup.date().nullable())
                .test("dateRange", i18n.get("", "Ingrese ambas fechas"), (value) => validateDateRange(value)),
        };
        return Yup.object().shape(validate);
    });

    return (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} ref={formRef} validationSchema={vepValidation}>
            {(allFormik) => (
                <Form>
                    <Row>
                        <Col xs={12} md={4}>
                            <Field
                                idForm={FORM_ID}
                                name="queryType"
                                component={Selector}
                                options={vepTypes.map((type) => ({
                                    value: type,
                                    label: i18n.get(`vep.type.${type}`),
                                }))}
                                label={i18n.get("vep.payment.type")}
                                placeholder={i18n.get("vep.filter.type.placeholder", "Seleccioná una opción")}
                                onChange={handleChangeType}
                            />
                        </Col>
                        {selectedType && (selectedType === vepTypes[1] || selectedType === vepTypes[2]) && (
                            <Col className="required" xs={12} md={4}>
                                <Field
                                    component={TextField}
                                    labelText="vep.payment.cuitCreator"
                                    placeholderText=""
                                    idForm={FORM_ID}
                                    name="cuitOrigin"
                                    type="text"
                                    pattern="[0-9]*"
                                    maxLength={11}
                                    minLength={0}
                                />
                            </Col>
                        )}
                        {selectedType && selectedType === vepTypes[2] && (
                            <>
                                <Col xs={12} md={4}>
                                    <Field
                                        component={TextField}
                                        labelText="vep.payment.cuitTaxpayer"
                                        placeholderText=""
                                        idForm={FORM_ID}
                                        name="cuitTaxpayer"
                                        type="text"
                                        pattern="[0-9]*"
                                        maxLength={11}
                                        minLength={0}
                                    />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Field
                                        component={TextField}
                                        labelText="vep.payment.number"
                                        placeholderText=""
                                        idForm={FORM_ID}
                                        name="vepId"
                                        type="text"
                                        pattern="[0-9]*"
                                        maxLength={vepMaxLength}
                                        minLength={0}
                                    />
                                </Col>
                            </>
                        )}
                        {selectedType && (
                            <Col xs={12} md={4}>
                                <Field
                                    component={DateRangeField}
                                    idForm={FORM_ID}
                                    defaultLabel={i18n.get("vep.filter.dateRange.label", "Fecha desde - hasta")}
                                    minDate={getLimitDate(-3650)}
                                    maxDate={getLimitDate(3650)}
                                    name="dateRange"
                                    dateFormat="dd/MM/yyyy"
                                    selectsRange
                                    showMonthYearDropdown
                                    scrollableMonthYearDropdown
                                    popperPlacement="bottom"
                                    showGlobalPlaceholder
                                />
                            </Col>
                        )}
                        <Col className="filter-button" xs={12} md={4}>
                            <Button type="submit" className="btn-primary" label="global.search" loading={loading} />
                        </Col>
                    </Row>
                </Form>
            )}
        </Formik>
    );
};

FilterForm.propTypes = {
    onSubmit: func.isRequired,
    isVepInProgress: bool,
    loading: bool,
    activeUserCuil: string.isRequired,
    activeEnvironment: shape({}),
};

FilterForm.defaultProps = {
    isVepInProgress: false,
    loading: false,
    activeEnvironment: null,
};

const mapStateToProps = (state) => ({
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
});

export default connect(mapStateToProps)(FilterForm);
